import { useCallback, useMemo, useState } from 'react';

import InventoryAssetTree from '@atom/components/common/inventoryAssetTree/InventoryAssetTree';
import { Modal } from '@atom/mui';
import { InventoryCategoryTree } from '@atom/types/inventory';
import { SchemaTree } from '@atom/types/schema';
import { getCategoryIdPath } from '@atom/utilities/categoryUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

type ChatAssetDraftCategoryModalProps = {
  open: boolean;
  schema: SchemaTree;
  categoryId: string;
  categoryTree: InventoryCategoryTree | null;
  onSelect: (category: InventoryCategoryTree) => void;
  onClose: () => void;
};

const ChatAssetDraftCategoryModal = ({
  open,
  schema,
  categoryId,
  categoryTree,
  onSelect,
  onClose,
}: ChatAssetDraftCategoryModalProps) => {
  const [selectedCategory, setSelectedCategory] =
    useState<InventoryCategoryTree | null>(null);

  const initialOpenCategories = useMemo(() => {
    if (!categoryTree || !categoryId) {
      return undefined;
    }

    const ancestors =
      getCategoryIdPath(categoryTree, categoryId)?.slice(0, -1) ?? [];

    return new Set(ancestors);
  }, [categoryTree, categoryId]);

  const handleCategoryClick = useCallback((category: InventoryCategoryTree) => {
    if (isNilOrEmpty(category?.categories)) {
      setSelectedCategory(category);
    }
  }, []);

  const handleConfirm = useCallback(() => {
    if (selectedCategory) {
      onSelect(selectedCategory);
      setSelectedCategory(null);
    }
  }, [selectedCategory, onSelect]);

  const handleCancel = useCallback(() => {
    setSelectedCategory(null);
    onClose();
  }, [onClose]);

  return (
    <Modal
      open={open}
      title="Select Category"
      width="lg"
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      confirmButtonText="Select"
      disabled={!selectedCategory}
      contentStyle={{
        padding: '1rem 1rem 0',
        height: '25rem',
      }}
    >
      {open && (
        <InventoryAssetTree
          includeAssets={false}
          search="categories"
          onCategoryClick={handleCategoryClick}
          onCategoryExpand={() => {}}
          openCategories={initialOpenCategories}
          disabledCategoryIds={new Set(['inventory'])}
          schemaId={schema.id}
          selectedCategoryId={selectedCategory?.id ?? categoryId}
          includeMaterials={schema.isMaterial}
          includeLocationBased={!schema.isMaterial}
          onlyPublishedCategories
          isAssetCreate
        />
      )}
    </Modal>
  );
};

export default ChatAssetDraftCategoryModal;
